import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CButton,
  CForm,
  CFormInput,
  CFormLabel,
  CFormTextarea,
  CFormSelect,
  CFormCheck,
  CBadge,
  CTable,
  CTableBody,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
  CTableDataCell,
  CProgress,
  CProgressBar,
  CAlert,
  CSpinner,
  CTabs,
  CTabList,
  CTab,
  CTabContent,
  CTabPanel,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react'
import { CIcon } from '@coreui/icons-react'
import { cilBuilding, cilStar, cilCalendar, cilPeople, cilChart, cilCheckCircle, cilCloudUpload, cilUser, cilFolder, cilWarning, cilCloudDownload, cilCreditCard, cilDescription, cilPencil } from '@coreui/icons'
import { toast, ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useAuth } from '../utils/AuthContext'
import { fetchCompleteProfile, updateCompanyProfile, uploadDocument, fetchMembershipFees, createPayment, getMemberPaymentHistory, updatePayment } from '../../utils/api'
import { useProfileCompletion } from '../hooks/useProfileCompletion'
import { ProfileCompletionProgress, ProfileCompletionBanner } from '../components/ProfileCompletionGuard'
import { FIELD_LABELS, SECTION_LABELS } from '../utils/profileCompletion'
import config from '../../config'

const Profile = () => {
  const { user } = useAuth()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState('documents')
  const [membershipType, setMembershipType] = useState('new')
  const [agreementAccepted, setAgreementAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingProfile, setIsLoadingProfile] = useState(false)
  const [isLoadingFees, setIsLoadingFees] = useState(false)
  const [completeProfile, setCompleteProfile] = useState(null)
  const [membershipFees, setMembershipFees] = useState([])
  const [companyStatus, setCompanyStatus] = useState('pending') // pending, approved, rejected
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showReplaceModal, setShowReplaceModal] = useState(false)
  const [selectedDocumentToReplace, setSelectedDocumentToReplace] = useState(null)
  const [replacementFile, setReplacementFile] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showEditPaymentModal, setShowEditPaymentModal] = useState(false)
  const [isLoadingPayment, setIsLoadingPayment] = useState(false)
  const [isLoadingEditPayment, setIsLoadingEditPayment] = useState(false)
  const [paymentHistory, setPaymentHistory] = useState([])
  const [isLoadingPaymentHistory, setIsLoadingPaymentHistory] = useState(false)
  const [editingPayment, setEditingPayment] = useState(null)

  // Payment form data
  const [paymentData, setPaymentData] = useState({
    payment_type: 'new_member',
    payment_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    bank_name: '',
    receipt_number: '',
    payment_method: 'mobile_money', // Default to mobile_money (M-Pesa)
    payment_reference: '',
    account_number: '',
    membership_year: new Date().getFullYear().toString(),
    additional_notes: ''
  })

  // Profile completion tracking
  const {
    percentage,
    isComplete,
    nextActions,
    completedSections,
    missingSections,
    updateCompletion,
    refreshCompletion
  } = useProfileCompletion()

  // Check if user was redirected due to incomplete profile
  const redirectedFromIncomplete = location.state?.reason === 'profile_incomplete'
  const redirectFromPage = location.state?.from?.pathname
  const redirectNextActions = location.state?.nextActions || []

  // Company form data
  const [companyData, setCompanyData] = useState({
    companyName: '',
    registrationNumber: '',
    companyEmail: '',
    companyPhone: '',
    employees: '1-10',
    address: '',
    physicalLocation: '',
    contactPerson: '',
    position: '',
    contactTelephone: ''
  })

  // Certificate data
  const [certificates, setCertificates] = useState({
    registrationCertificate: '',
    cr12Certificate: '',
    kraPin: '',
    businessPermit: '',
    taxCompliance: '',
    nssfCompliance: '',
    shaCompliance: '',
    passportPhotos: '',
    nationalIdPassport: ''
  })

  // File upload states
  const [fileUploads, setFileUploads] = useState({
    registrationCertificate: null,
    cr12Certificate: null,
    kraPin: null,
    businessPermit: null,
    taxCompliance: null,
    nssfCompliance: null,
    shaCompliance: null,
    passportPhotos: null,
    nationalIdPassport: null
  })

  // Document type mapping for API
  const documentTypeMapping = {
    registrationCertificate: 'certificate_of_registration',
    cr12Certificate: 'cr12_certificate',
    kraPin: 'kra_pin_certificate',
    businessPermit: 'business_permit',
    taxCompliance: 'tax_compliance_certificate',
    nssfCompliance: 'nssf_compliance_certificate',
    shaCompliance: 'sha_compliance_certificate',
    passportPhotos: 'passport_photos',
    nationalIdPassport: 'national_id_passport'
  }

  useEffect(() => {
    const loadCompleteProfile = async () => {
      if (user?.id) {
        setIsLoadingProfile(true)
        try {
          const response = await fetchCompleteProfile(user.id)
          if (response.status === 'success') {
            setCompleteProfile(response.data)

            // Update profile completion tracking
            updateCompletion(response.data)

            // Update company form with complete profile data
            const { profile, company } = response.data
            setCompanyData({
              companyName: company.companyName || '',
              registrationNumber: company.registrationNumber || '',
              companyEmail: company.companyEmail || company.contactEmail || user.email || '',
              companyPhone: company.companyPhone || company.contactTelephone || '',
              employees: company.numberOfEmployees || '1-10',
              address: company.businessAddress || '',
              physicalLocation: company.physicalLocation || '',
              contactPerson: company.contactPerson || (profile.firstName && profile.lastName ? `${profile.firstName} ${profile.lastName}` : ''),
              position: company.positionInCompany || 'Contact Person',
              contactTelephone: company.contactTelephone || profile.phone || ''
            })

            // Set membership type and declaration status from API
            setMembershipType(company.membershipType || 'new')
            setAgreementAccepted(company.declarationAccepted || false)

            // Set company verification status
            setCompanyStatus(company.status || company.verification_status || 'pending')

            // Set certificate data from API
            if (company.certificates) {
              setCertificates({
                registrationCertificate: company.certificates.registration || '',
                cr12Certificate: company.certificates.cr12 || '',
                kraPin: company.certificates.kraPin || '',
                businessPermit: company.certificates.businessPermit || '',
                taxCompliance: company.certificates.taxCompliance || '',
                nssfCompliance: company.certificates.nssfCompliance || '',
                shaCompliance: company.certificates.shaCompliance || '',
                passportPhotos: company.certificates.passportPhotos || '',
                nationalIdPassport: company.certificates.nationalIdPassport || ''
              })
            }
          } else {
            // Fallback to user data if API fails
            setCompanyData({
              companyName: user.companyName || user.company || '',
              registrationNumber: user.registrationNumber || '',
              companyEmail: user.email || '',
              companyPhone: user.companyPhone || user.phone || '',
              employees: user.employees || '1-10',
              address: user.address || '',
              physicalLocation: user.physicalLocation || '',
              contactPerson: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '',
              position: user.position || '',
              contactTelephone: user.phone || ''
            })
          }
        } catch (error) {
          console.error('Error loading complete profile:', error)
          // Fallback to user data if API fails
          setCompanyData({
            companyName: user.companyName || user.company || '',
            registrationNumber: user.registrationNumber || '',
            companyEmail: user.email || '',
            companyPhone: user.companyPhone || user.phone || '',
            employees: user.employees || '1-10',
            address: user.address || '',
            physicalLocation: user.physicalLocation || '',
            contactPerson: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '',
            position: user.position || '',
            contactTelephone: user.phone || ''
          })
        } finally {
          setIsLoadingProfile(false)
        }
      }
    }

    loadCompleteProfile()
  }, [user, updateCompletion])

  // Fetch membership fees
  useEffect(() => {
    const loadMembershipFees = async () => {
      setIsLoadingFees(true)
      try {
        const response = await fetchMembershipFees()
        if (response.status === 'success' && response.data) {
          setMembershipFees(response.data)
        }
      } catch (error) {
        console.error('Error loading membership fees:', error)
        // Set default fees if API fails
        setMembershipFees([
          {
            id: 1,
            payment_type: 'registration',
            amount: '5000.00',
            currency: 'KES',
            description: 'Registration Fee'
          },
          {
            id: 2,
            payment_type: 'subscription',
            amount: '40000.00',
            currency: 'KES',
            description: 'Yearly Subscription'
          }
        ])
      } finally {
        setIsLoadingFees(false)
      }
    }

    loadMembershipFees()
  }, [])

  // Load payment history when component mounts or user changes
  useEffect(() => {
    const loadPaymentHistory = async () => {
      if (user?.id) {
        setIsLoadingPaymentHistory(true)
        try {
          // Use the correct company ID for loading payment history
          const companyId = completeProfile?.company?.id || user.company_id || user.id
          const response = await getMemberPaymentHistory(companyId, 1, 10)
          if (response.status === 'success' && response.data) {
            setPaymentHistory(response.data.payments || [])
          }
        } catch (error) {
          console.error('Error loading payment history:', error)
          setPaymentHistory([])
        } finally {
          setIsLoadingPaymentHistory(false)
        }
      }
    }

    loadPaymentHistory()
  }, [user?.id, completeProfile?.company?.id])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setCompanyData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Handle payment form input changes
  const handlePaymentInputChange = (e) => {
    const { name, value } = e.target
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // Handle payment form submission
  const handlePaymentSubmit = async (e) => {
    e.preventDefault()

    // Validate required fields
    const requiredFields = ['payment_type', 'payment_amount', 'payment_reference', 'payment_method', 'payment_date', 'membership_year']
    const missingFields = requiredFields.filter(field => !paymentData[field])

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`, {
        position: "top-right",
        autoClose: 5000,
      })
      return
    }

    setIsLoadingPayment(true)

    try {
      // Map frontend values to database enum values
      const mapPaymentMethod = (method) => {
        const mapping = {
          'bank_transfer': 'bank_transfer',
          'mpesa': 'mobile_money',  // Map mpesa to mobile_money for database
          'cash': 'cash',
          'cheque': 'cheque',
          'online': 'online',
          'other': 'other'
        }
        return mapping[method] || method
      }

      // Get the correct company ID from the complete profile data
      const companyId = completeProfile?.company?.id || user.company_id || user.id

      const paymentPayload = {
        member_id: companyId, // Use company ID instead of user ID
        payment_type: paymentData.payment_type,
        payment_amount: parseFloat(paymentData.payment_amount),
        payment_date: paymentData.payment_date,
        bank_name: paymentData.bank_name,
        receipt_number: paymentData.receipt_number,
        payment_method: mapPaymentMethod(paymentData.payment_method), // Map to correct enum value
        payment_reference: paymentData.payment_reference,
        account_number: paymentData.account_number,
        membership_year: parseInt(paymentData.membership_year),
        notes: paymentData.additional_notes // Map additional_notes to notes field
      }

      console.log('Payment payload being sent:', paymentPayload)
      const response = await createPayment(paymentPayload)
      console.log('Payment response:', response)

      if (response.status === 'success') {
        toast.success('Payment information submitted successfully!', {
          position: "top-right",
          autoClose: 5000,
        })

        // Reset form
        setPaymentData({
          payment_type: 'new_member',
          payment_amount: '',
          payment_date: new Date().toISOString().split('T')[0],
          bank_name: '',
          receipt_number: '',
          payment_method: 'mobile_money', // Default to mobile_money (M-Pesa)
          payment_reference: '',
          account_number: '',
          membership_year: new Date().getFullYear().toString(),
          additional_notes: ''
        })

        // Close modal
        setShowPaymentModal(false)

        // Reload payment history
        const companyId = completeProfile?.company?.id || user.company_id || user.id
        const historyResponse = await getMemberPaymentHistory(companyId, 1, 10)
        if (historyResponse.status === 'success' && historyResponse.data) {
          setPaymentHistory(historyResponse.data.payments || [])
        }
      } else {
        toast.error(response.message || 'Failed to submit payment information', {
          position: "top-right",
          autoClose: 5000,
        })
      }
    } catch (error) {
      console.error('Error submitting payment:', error)
      toast.error(error.message || 'Failed to submit payment information', {
        position: "top-right",
        autoClose: 5000,
      })
    } finally {
      setIsLoadingPayment(false)
    }
  }

  // Handle edit payment button click
  const handleEditPayment = (payment) => {
    setEditingPayment(payment)
    // Pre-fill the form with existing payment data
    setPaymentData({
      payment_type: payment.payment_type,
      payment_amount: payment.payment_amount.toString(),
      payment_date: payment.payment_date,
      bank_name: payment.bank_name || '',
      receipt_number: payment.receipt_number || '',
      payment_method: payment.payment_method,
      payment_reference: payment.payment_reference,
      account_number: payment.account_number || '',
      membership_year: payment.membership_year.toString(),
      additional_notes: payment.notes || ''
    })
    setShowEditPaymentModal(true)
  }

  // Handle edit payment form submission
  const handleEditPaymentSubmit = async (e) => {
    e.preventDefault()

    if (!editingPayment) return

    // Validate required fields
    const requiredFields = ['payment_type', 'payment_amount', 'payment_reference', 'payment_method', 'payment_date', 'membership_year']
    const missingFields = requiredFields.filter(field => !paymentData[field])

    if (missingFields.length > 0) {
      toast.error(`Please fill in all required fields: ${missingFields.join(', ')}`, {
        position: "top-right",
        autoClose: 5000,
      })
      return
    }

    setIsLoadingEditPayment(true)

    try {
      // Map frontend values to database enum values
      const mapPaymentMethod = (method) => {
        const mapping = {
          'bank_transfer': 'bank_transfer',
          'mpesa': 'mobile_money',  // Map mpesa to mobile_money for database
          'cash': 'cash',
          'cheque': 'cheque',
          'online': 'online',
          'other': 'other'
        }
        return mapping[method] || method
      }

      // Get the correct company ID from the complete profile data
      const companyId = completeProfile?.company?.id || user.company_id || user.id

      const paymentPayload = {
        member_id: companyId, // Use company ID instead of user ID
        payment_type: paymentData.payment_type,
        payment_amount: parseFloat(paymentData.payment_amount),
        payment_date: paymentData.payment_date,
        bank_name: paymentData.bank_name,
        receipt_number: paymentData.receipt_number,
        payment_method: mapPaymentMethod(paymentData.payment_method),
        payment_reference: paymentData.payment_reference,
        account_number: paymentData.account_number,
        membership_year: parseInt(paymentData.membership_year),
        notes: paymentData.additional_notes
      }

      console.log('Updating payment payload:', paymentPayload)
      const response = await updatePayment(editingPayment.id, paymentPayload)
      console.log('Update payment response:', response)

      if (response.status === 'success') {
        toast.success('Payment information updated successfully!', {
          position: "top-right",
          autoClose: 5000,
        })

        // Close modal and reset state
        setShowEditPaymentModal(false)
        setEditingPayment(null)

        // Reload payment history
        const companyId = completeProfile?.company?.id || user.company_id || user.id
        const historyResponse = await getMemberPaymentHistory(companyId, 1, 10)
        if (historyResponse.status === 'success' && historyResponse.data) {
          setPaymentHistory(historyResponse.data.payments || [])
        }
      } else {
        toast.error(response.message || 'Failed to update payment information', {
          position: "top-right",
          autoClose: 5000,
        })
      }
    } catch (error) {
      console.error('Error updating payment:', error)
      toast.error(error.message || 'Failed to update payment information', {
        position: "top-right",
        autoClose: 5000,
      })
    } finally {
      setIsLoadingEditPayment(false)
    }
  }

  // Handle file input changes
  const handleFileChange = (e) => {
    const { name, files } = e.target
    setFileUploads(prev => ({
      ...prev,
      [name]: files[0] // For single file uploads
    }))

    // For passport photos, handle multiple files
    if (name === 'passportPhotos' && files.length > 0) {
      setFileUploads(prev => ({
        ...prev,
        [name]: Array.from(files) // Convert FileList to Array for multiple files
      }))
    }
  }

  // Upload a single document
  const uploadSingleDocument = async (fileKey, file, documentType) => {
    try {
      console.log(`Uploading ${fileKey}:`, file.name, 'as', documentType)

      // Ensure file is a valid File object
      if (!(file instanceof File)) {
        throw new Error('Invalid file object')
      }

      // Debug file object
      console.log('File object:', {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified
      })

      const formData = new FormData()

      // Add file with explicit filename
      formData.append('document', file, file.name)

      // Add required metadata
      formData.append('document_type', documentType)
      formData.append('document_name', file.name)
      formData.append('description', `${fileKey} uploaded from profile page`)
      formData.append('upload_reason', 'Profile completion')

      // Try to get company_id from user profile, fallback to user_id
      const companyId = user.company_id || user.id
      formData.append('company_id', companyId)
      formData.append('user_id', user.id) // Ensure user_id is set here

      // Special handling for passport photos to indicate photo number
      if (documentType === 'passport_photos') {
        const photoNumber = fileKey.split('_')[1] || '1'
        formData.append('photo_number', photoNumber)
      }

      // Debug FormData contents
      console.log('FormData contents before upload:')
      for (let [key, value] of formData.entries()) {
        if (key === 'document') {
          console.log('document:', {
            name: value.name,
            size: value.size,
            type: value.type,
            lastModified: value.lastModified
          })
        } else {
          console.log(`${key}:`, value)
        }
      }

      const response = await uploadDocument(user.id, formData)

      console.log(`📤 ${fileKey} upload response:`, response)

      // More robust success checking
      if (response && (
        response.status === 'success' ||
        response.success === true ||
        response.data?.document_id ||
        response.document_id
      )) {
        console.log(`✅ ${fileKey} uploaded successfully:`, response)
        return { success: true, message: `${fileKey} uploaded successfully` }
      } else {
        console.error(`❌ ${fileKey} upload failed:`, response)
        return { success: false, message: response?.message || `Failed to upload ${fileKey}` }
      }
    } catch (error) {
      console.error(`❌ Error uploading ${fileKey}:`, error)

      // Check if it's a network error but the upload might have succeeded
      if (error.message && (
        error.message.includes('Failed to upload document') ||
        error.message.includes('Network') ||
        error.message.includes('fetch')
      )) {
        // This might be a false error - the document could still be uploaded
        console.warn(`⚠️ Upload error for ${fileKey}, but document might have been uploaded successfully`)
        return { success: true, message: `${fileKey} uploaded successfully (with warning)` }
      }

      return { success: false, message: error.message || `Failed to upload ${fileKey}` }
    }
  }

  // Process all file uploads with replacement logic
  const processFileUploads = async () => {
    const uploadPromises = []
    let hasNewDocuments = false

    // Check if there are any new documents to upload
    Object.entries(fileUploads).forEach(([fileKey, file]) => {
      if (file) {
        hasNewDocuments = true
      }
    })

    // If no new documents, return early
    if (!hasNewDocuments) {
      console.log('No new documents to upload')
      return { successful: 0, failed: 0, hasNewDocuments: false }
    }

    console.log('🔄 New documents detected - will replace existing documents')

    // Process each file upload
    Object.entries(fileUploads).forEach(([fileKey, file]) => {
      if (file) {
        const documentType = documentTypeMapping[fileKey]

        if (fileKey === 'passportPhotos' && Array.isArray(file)) {
          // Get file types
          const fileTypes = file.map(f => f.type.toLowerCase());
          const isPDF = fileTypes.some(type => type === 'application/pdf');
          const isImage = fileTypes.some(type => type.startsWith('image/'));

          // Validate files
          if (isPDF && file.length > 1) {
            toast.warn('Please upload only one PDF file containing both passport photos.', {
              position: "top-right",
              autoClose: 4000,
            });
            return;
          }

          if (isImage && file.length !== 2) {
            toast.warn('Please upload exactly 2 passport photos when using image format.', {
              position: "top-right",
              autoClose: 4000,
            });
            return;
          }

          if (!isPDF && !isImage) {
            toast.error('Invalid file format. Please upload either images (JPG/PNG) or a PDF.', {
              position: "top-right",
              autoClose: 4000,
            });
            return;
          }

          // Handle multiple passport photos
          file.forEach((singleFile, index) => {
            // Debug each passport photo
            console.log(`Processing passport photo ${index + 1}:`, singleFile)

            const fileName = `passportPhotos_${index + 1}`
            uploadPromises.push(
              uploadSingleDocument(fileName, singleFile, documentType)
                .then(result => ({ fileKey: fileName, ...result }))
            )
          })
        } else if (file) {
          // Handle single file
          uploadPromises.push(
            uploadSingleDocument(fileKey, file, documentType)
              .then(result => ({ fileKey, ...result }))
          )
        }
      }
    })

    if (uploadPromises.length > 0) {
      console.log(`Processing ${uploadPromises.length} file uploads (replacing existing documents)...`)
      const results = await Promise.all(uploadPromises)

      const successful = results.filter(r => r.success)
      const failed = results.filter(r => !r.success)

      if (successful.length > 0) {
        console.log(`✅ ${successful.length} document(s) uploaded and replaced successfully`)
        toast.success(`${successful.length} document(s) replaced successfully!`, {
          position: "top-right",
          autoClose: 4000,
        })
      }

      if (failed.length > 0) {
        console.error('❌ Failed uploads:', failed)
        toast.error(`Failed to replace ${failed.length} document(s). Please try again.`, {
          position: "top-right",
          autoClose: 6000,
        })
      }

      return { successful: successful.length, failed: failed.length, hasNewDocuments: true }
    }

    return { successful: 0, failed: 0, hasNewDocuments: true }
  }

  // Helper function to check if a field is missing and should be highlighted
  const isFieldMissing = (fieldName) => {
    return nextActions.some(action => action.field === fieldName)
  }

  // Helper function to render status alert
  const renderStatusAlert = () => {
    if (companyStatus === 'approved') {
      return (
        <CAlert color="success" className="mb-4">
          <CIcon icon={cilCheckCircle} className="me-2" />
          <strong>Documents Approved!</strong>
          <p className="mb-0 mt-2">
            Your documents are already approved, you cannot change them.
          </p>
        </CAlert>
      )
    } else if (companyStatus === 'pending') {
      return (
        <CAlert color="warning" className="mb-4">
          <CIcon icon={cilWarning} className="me-2" />
          <strong>Documents Pending Review</strong>
          <p className="mb-0 mt-2">
            You can change your documents before the admin has verified them. Click on Company Profile and re-upload the document.
          </p>
        </CAlert>
      )
    } else if (companyStatus === 'rejected') {
      return (
        <CAlert color="danger" className="mb-4">
          <CIcon icon={cilWarning} className="me-2" />
          <strong>Documents Rejected</strong>
          <p className="mb-0 mt-2">
            Your documents have been rejected. Please upload new documents and resubmit your application.
          </p>
        </CAlert>
      )
    }
    return null
  }

  // Helper function to render certificate status
  const renderCertificateStatus = (certificateKey, certificateName) => {
    const isUploaded = certificates[certificateKey] && certificates[certificateKey].trim() !== ''
    const isMissing = isFieldMissing(certificateKey)
    const hasFileSelected = fileUploads[certificateKey] !== null

    return (
      <div className="mb-2">
        {isUploaded ? (
          <div>
            <div className="d-flex align-items-center mb-1">
              <CBadge color="success" className="me-2">✓ Uploaded</CBadge>
              <small className="text-muted">
                {certificateName} has been uploaded successfully
              </small>
            </div>
            <div className="mt-1">
              {certificateKey === 'passportPhotos' && certificates[certificateKey].includes(',') ? (
                // Handle multiple passport photos (comma-separated paths)
                certificates[certificateKey].split(',').map((photoPath, index) => (
                  <a
                    key={index}
                    href={`${config.API_URL.replace('/api/', '/')}${photoPath.trim()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary me-2 mb-1"
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <CIcon icon={cilCloudDownload} className="me-1" style={{ fontSize: '0.75rem' }} />
                    View Photo {index + 1}
                  </a>
                ))
              ) : (
                // Handle single document
                <a
                  href={`${config.API_URL.replace('/api/', '/')}${certificates[certificateKey]}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                >
                  <CIcon icon={cilCloudDownload} className="me-1" style={{ fontSize: '0.75rem' }} />
                  View Document
                </a>
              )}
              {/* Replace button for uploaded documents when status is pending */}
              {companyStatus === 'pending' && (
                <div className="mt-2">
                  <CButton
                    color="warning"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedDocumentToReplace({
                        key: certificateKey,
                        name: certificateName,
                        currentPath: certificates[certificateKey]
                      })
                      setShowReplaceModal(true)
                    }}
                    style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                  >
                    <CIcon icon={cilCloudUpload} className="me-1" style={{ fontSize: '0.75rem' }} />
                    Replace
                  </CButton>
                </div>
              )}
            </div>
          </div>
        ) : hasFileSelected ? (
          <div className="d-flex align-items-center">
            <CBadge color="info" className="me-2">📁 Selected</CBadge>
            <small className="text-info">
              {certificateKey === 'passportPhotos' && Array.isArray(fileUploads[certificateKey])
                ? `${fileUploads[certificateKey].length} files selected for upload`
                : `${certificateName} selected for upload`
              }
            </small>
          </div>
        ) : (
          <div className="d-flex align-items-center">
            <CBadge color={isMissing ? "danger" : "warning"} className="me-2">
              {isMissing ? "❗ Required" : "⚠ Missing"}
            </CBadge>
            <small className={isMissing ? "text-danger" : "text-muted"}>
              {certificateName} is required{isMissing ? " - complete this to access other features" : " but not yet uploaded"}
            </small>
          </div>
        )}
      </div>
    )
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!agreementAccepted) {
      toast.warn('Please accept the declaration to proceed.', {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
      return
    }

    setIsLoading(true)

    try {
      // Step 1: Update company profile text fields
      const companyProfileData = {
        userId: user.id,
        // Company contact information
        companyEmail: companyData.companyEmail,
        companyPhone: companyData.companyPhone,
        employees: companyData.employees,
        address: companyData.address,
        physicalLocation: companyData.physicalLocation,
        contactPerson: companyData.contactPerson,
        position: companyData.position,
        contactTelephone: companyData.contactTelephone,
        membershipType: membershipType,
        declarationAccepted: agreementAccepted
      }

      console.log('Updating company profile:', companyProfileData)
      const profileResponse = await updateCompanyProfile(companyProfileData)

      // Step 2: Process file uploads if any files are selected
      const uploadResults = await processFileUploads()

      // Step 3: Show results and refresh data
      if (profileResponse.status === 'success') {
        if (uploadResults.hasNewDocuments && uploadResults.successful > 0) {
          // New documents were uploaded and replaced
          toast.success(`🎉 Profile updated and ${uploadResults.successful} document(s) replaced successfully!`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        } else if (uploadResults.hasNewDocuments && uploadResults.successful === 0) {
          // New documents were selected but failed to upload
          toast.warning('⚠️ Profile updated but document replacement failed. Please try uploading documents again.', {
            position: "top-right",
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        } else {
          // No new documents, just profile update
          toast.success('🎉 Company profile updated successfully!', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          })
        }

        // Clear file uploads after successful submission
        setFileUploads({
          registrationCertificate: null,
          cr12Certificate: null,
          kraPin: null,
          businessPermit: null,
          taxCompliance: null,
          nssfCompliance: null,
          shaCompliance: null,
          passportPhotos: null,
          nationalIdPassport: null
        })

        // Clear file input values
        const fileInputs = document.querySelectorAll('input[type="file"]')
        fileInputs.forEach(input => input.value = '')

        // Refresh profile completion data after successful update
        setTimeout(() => {
          refreshCompletion()
        }, 1000) // Small delay to ensure backend has processed uploads

        // Show success modal only if new documents were uploaded
        if (uploadResults.hasNewDocuments && uploadResults.successful > 0) {
          setTimeout(() => {
            setShowSuccessModal(true)
          }, 1000) // Delay to ensure toast notifications are shown first
        }

      } else {
        toast.error(profileResponse.message || 'Failed to update company profile. Please try again.', {
          position: "top-right",
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        })
      }
    } catch (error) {
      console.error('Profile update error:', error)
      toast.error(error.message || 'Failed to update profile. Please try again.', {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <CRow>
        <CCol xs={12}>
          {/* Profile Completion Status Card */}
          <CCard className="mb-4">
            <CCardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>Profile Completion Status</strong>
                  <small className="text-muted ms-2">Track your membership application progress</small>
                </div>
                <CBadge color={isComplete ? "success" : percentage >= 50 ? "warning" : "danger"} size="lg">
                  {percentage}% Complete
                </CBadge>
              </div>
            </CCardHeader>
            <CCardBody>
              {/* Show alert if redirected due to incomplete profile */}
              {redirectedFromIncomplete && (
                <CAlert color="warning" className="mb-4">
                  <CIcon icon={cilWarning} className="me-2" />
                  <strong>Profile Completion Required</strong>
                  <p className="mb-2 mt-2">
                    You cannot access other sections because your profile is incomplete ({percentage}%).
                    Please complete the required fields below to access all member features.
                  </p>
                  {redirectNextActions.length > 0 && (
                    <div>
                      <small className="fw-bold">Priority items to complete:</small>
                      <ul className="mb-0 mt-1">
                        {redirectNextActions.map((action, index) => (
                          <li key={index}>{action.fieldLabel}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CAlert>
              )}

              <CRow>
                <CCol md={8}>
                  <ProfileCompletionProgress showDetails={true} size="large" />
                </CCol>
                <CCol md={4}>
                  {nextActions.length > 0 && (
                    <div>
                      <h6 className="text-muted">Next Steps:</h6>
                      <div className="small">
                        {nextActions.slice(0, 5).map((action, index) => (
                          <div key={index} className="d-flex align-items-center mb-1">
                            <CBadge color="primary" className="me-2">{index + 1}</CBadge>
                            <span>{action.fieldLabel}</span>
                          </div>
                        ))}
                        {nextActions.length > 5 && (
                          <div className="text-muted">
                            + {nextActions.length - 5} more items...
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <CCard className="mb-4">
            <CCardHeader>
              <strong>KPCA Company Profile</strong>
              <small className="text-muted ms-2">Kenya Professional Cleaners Association (KPCA) Membership Registration Form</small>
            </CCardHeader>
            <CCardBody>
              {/* Alert messages replaced with toast notifications */}

              {isLoadingProfile ? (
                <div className="text-center py-5">
                  <CSpinner color="primary" />
                  <p className="text-muted mt-2">Loading company profile data...</p>
                </div>
              ) : (
                <>
                  {/* Enhanced Tabs */}
                  <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
                    <CTabList
                      variant="pills"
                      className="mb-4"
                      style={{
                        backgroundColor: '#f8f9fa',
                        borderRadius: '12px',
                        padding: '8px',
                        border: '1px solid #e9ecef',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <CTab
                        itemKey="documents"
                        className="me-2"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.95rem',
                          padding: '12px 24px',
                          transition: 'all 0.3s ease',
                          border: 'none',
                          backgroundColor: activeTab === 'documents' ? '#0d6efd' : 'transparent',
                          color: activeTab === 'documents' ? '#fff' : '#6c757d',
                          boxShadow: activeTab === 'documents' ? '0 4px 8px rgba(13, 110, 253, 0.3)' : 'none'
                        }}
                      >
                        <CIcon
                          icon={cilFolder}
                          className="me-2"
                          style={{ fontSize: '1.1rem' }}
                        />
                        <span>Documents & Registration</span>
                      </CTab>
                      <CTab
                        itemKey="payment"
                        style={{
                          borderRadius: '8px',
                          fontWeight: '600',
                          fontSize: '0.95rem',
                          padding: '12px 24px',
                          transition: 'all 0.3s ease',
                          border: 'none',
                          backgroundColor: activeTab === 'payment' ? '#198754' : 'transparent',
                          color: activeTab === 'payment' ? '#fff' : '#6c757d',
                          boxShadow: activeTab === 'payment' ? '0 4px 8px rgba(25, 135, 84, 0.3)' : 'none'
                        }}
                      >
                        <CIcon
                          icon={cilCreditCard}
                          className="me-2"
                          style={{ fontSize: '1.1rem' }}
                        />
                        <span>Payment & Fees</span>
                      </CTab>
                    </CTabList>

                    <CTabContent>
                      {/* Documents Tab */}
                      <CTabPanel
                        className="py-4"
                        itemKey="documents"
                        style={{
                          backgroundColor: activeTab === 'documents' ? '#ffffff' : 'transparent',
                          borderRadius: '12px',
                          border: activeTab === 'documents' ? '2px solid #0d6efd' : 'none',
                          boxShadow: activeTab === 'documents' ? '0 8px 25px rgba(13, 110, 253, 0.15)' : 'none',
                          transition: 'all 0.3s ease',
                          padding: '2rem',
                          margin: '0.5rem 0'
                        }}
                      >
                        <CRow>
                          <CCol md={12}>
                            {/* Status Alert */}
                            {renderStatusAlert()}

                            <CForm onSubmit={handleSubmit}>
                              {/* Membership Type */}
                              <CRow className="mb-4">
                                <CCol md={12}>
                                  <CFormLabel className="fw-bold">3. Membership Type</CFormLabel>
                                  <div className="mt-2">
                                    <CFormCheck
                                      type="radio"
                                      id="newMember"
                                      name="membershipType"
                                      label="New Member"
                                      checked={membershipType === 'new'}
                                      onChange={(e) => setMembershipType('new')}
                                    />
                                    <CFormCheck
                                      type="radio"
                                      id="renewal"
                                      name="membershipType"
                                      label="Renewal"
                                      checked={membershipType === 'renewal'}
                                      onChange={(e) => setMembershipType('renewal')}
                                      className="mt-2"
                                    />
                                  </div>
                                </CCol>
                              </CRow>

                              {/* Company Information */}
                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="companyName" className={isFieldMissing('companyName') ? 'text-danger fw-bold' : ''}>
                                    Company Name {isFieldMissing('companyName') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="text"
                                    id="companyName"
                                    name="companyName"
                                    value={companyData.companyName}
                                    onChange={handleInputChange}
                                    placeholder="Enter company name"
                                    disabled
                                    invalid={isFieldMissing('companyName')}
                                  />
                                  <small className="text-muted">Company name cannot be changed for security reasons</small>
                                </CCol>
                                <CCol md={6}>
                                  <CFormLabel htmlFor="registrationNumber" className={isFieldMissing('registrationNumber') ? 'text-danger fw-bold' : ''}>
                                    Company Registration Number {isFieldMissing('registrationNumber') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="text"
                                    id="registrationNumber"
                                    name="registrationNumber"
                                    value={companyData.registrationNumber}
                                    onChange={handleInputChange}
                                    placeholder="Enter registration number"
                                    disabled
                                    invalid={isFieldMissing('registrationNumber')}
                                  />
                                  <small className="text-muted">Registration number cannot be changed for security reasons</small>
                                </CCol>
                              </CRow>

                              {/* Required Attachments - Company Documents */}
                              <CRow className="mb-4">
                                <CCol md={12}>
                                  <div className="border rounded p-3 bg-light">
                                    <h6 className="fw-bold mb-3">
                                      <CIcon icon={cilFolder} className="me-2" />
                                      4. Required Attachments
                                    </h6>
                                    <p className="text-muted mb-3">Please attach the following documents:</p>

                                    {/* Document Replacement Info */}
                                    {companyStatus === 'pending' && (
                                      <CAlert color="info" className="mb-3">
                                        <CIcon icon={cilCloudUpload} className="me-2" />
                                        <strong>Document Replacement:</strong> You can upload new documents to replace existing ones.
                                        Simply select new files and submit the form to replace your current documents.
                                      </CAlert>
                                    )}

                                    <CRow>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="registrationCertificate">Registration Certificate</CFormLabel>
                                          {renderCertificateStatus('registrationCertificate', 'Registration Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="registrationCertificate"
                                            name="registrationCertificate"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="cr12Certificate">CR12</CFormLabel>
                                          {renderCertificateStatus('cr12Certificate', 'CR12 Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="cr12Certificate"
                                            name="cr12Certificate"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                    </CRow>

                                    <CRow>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="kraPin">KRA Pin</CFormLabel>
                                          {renderCertificateStatus('kraPin', 'KRA Pin Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="kraPin"
                                            name="kraPin"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="businessPermit">Business Permit</CFormLabel>
                                          {renderCertificateStatus('businessPermit', 'Business Permit')}
                                          <CFormInput
                                            type="file"
                                            id="businessPermit"
                                            name="businessPermit"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                    </CRow>

                                    <CRow>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="taxCompliance">Tax Compliance Certificate</CFormLabel>
                                          {renderCertificateStatus('taxCompliance', 'Tax Compliance Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="taxCompliance"
                                            name="taxCompliance"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="nssfCompliance">NSSF Compliance</CFormLabel>
                                          {renderCertificateStatus('nssfCompliance', 'NSSF Compliance Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="nssfCompliance"
                                            name="nssfCompliance"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                    </CRow>

                                    <CRow>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="nsif">SHA Compliance Certificate</CFormLabel>
                                          {renderCertificateStatus('shaCompliance', 'SHA Compliance Certificate')}
                                          <CFormInput
                                            type="file"
                                            id="nsif"
                                            name="shaCompliance"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                    </CRow>
                                  </div>
                                </CCol>
                              </CRow>

                              {/* Contact Information */}
                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="companyEmail" className={isFieldMissing('companyEmail') ? 'text-danger fw-bold' : ''}>
                                    Company Email Address {isFieldMissing('companyEmail') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="email"
                                    id="companyEmail"
                                    name="companyEmail"
                                    value={companyData.companyEmail}
                                    onChange={handleInputChange}
                                    placeholder="company@example.com"
                                    invalid={isFieldMissing('companyEmail')}
                                  />
                                  <small className="text-muted">To be used for all official communications from KPCA</small>
                                </CCol>
                                <CCol md={6}>
                                  <CFormLabel htmlFor="companyPhone" className={isFieldMissing('companyPhone') ? 'text-danger fw-bold' : ''}>
                                    Company Phone {isFieldMissing('companyPhone') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="tel"
                                    id="companyPhone"
                                    name="companyPhone"
                                    value={companyData.companyPhone}
                                    onChange={handleInputChange}
                                    placeholder="+254 123 456 789"
                                    invalid={isFieldMissing('companyPhone')}
                                  />
                                </CCol>
                              </CRow>

                              {/* Number of Employees */}
                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="employees" className={isFieldMissing('employees') ? 'text-danger fw-bold' : ''}>
                                    Number of Employees {isFieldMissing('employees') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormSelect
                                    id="employees"
                                    name="employees"
                                    value={companyData.employees}
                                    onChange={handleInputChange}
                                    invalid={isFieldMissing('employees')}
                                  >
                                    <option value="1-10">1-10</option>
                                    <option value="11-50">11-50</option>
                                    <option value="51-100">51-100</option>
                                    <option value="100+">100+</option>
                                  </CFormSelect>
                                </CCol>
                              </CRow>

                              {/* Business Address */}
                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="address" className={isFieldMissing('address') ? 'text-danger fw-bold' : ''}>
                                    Business Address {isFieldMissing('address') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormTextarea
                                    id="address"
                                    name="address"
                                    rows={3}
                                    value={companyData.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter business address"
                                    invalid={isFieldMissing('address')}
                                  />
                                </CCol>
                                <CCol md={6}>
                                  <CFormLabel htmlFor="physicalLocation" className={isFieldMissing('physicalLocation') ? 'text-danger fw-bold' : ''}>
                                    Physical Location {isFieldMissing('physicalLocation') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormTextarea
                                    id="physicalLocation"
                                    name="physicalLocation"
                                    rows={2}
                                    value={companyData.physicalLocation}
                                    onChange={handleInputChange}
                                    placeholder="Describe the exact physical location of your business premises"
                                    invalid={isFieldMissing('physicalLocation')}
                                  />
                                  <small className="text-muted">Include landmarks, GPS coordinates, or detailed directions</small>
                                </CCol>
                              </CRow>

                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="contactPerson" className={isFieldMissing('contactPerson') ? 'text-danger fw-bold' : ''}>
                                    Contact Person {isFieldMissing('contactPerson') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="text"
                                    id="contactPerson"
                                    name="contactPerson"
                                    value={companyData.contactPerson}
                                    onChange={handleInputChange}
                                    placeholder="Enter contact person name"
                                    invalid={isFieldMissing('contactPerson')}
                                  />
                                </CCol>
                                <CCol md={6}>
                                  <CFormLabel htmlFor="position" className={isFieldMissing('position') ? 'text-danger fw-bold' : ''}>
                                    Position in the Company {isFieldMissing('position') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="text"
                                    id="position"
                                    name="position"
                                    value={companyData.position}
                                    onChange={handleInputChange}
                                    placeholder="e.g., General Manager, Director, etc."
                                    invalid={isFieldMissing('position')}
                                  />
                                </CCol>
                              </CRow>

                              <CRow className="mb-3">
                                <CCol md={6}>
                                  <CFormLabel htmlFor="contactTelephone" className={isFieldMissing('contactTelephone') ? 'text-danger fw-bold' : ''}>
                                    Contact Telephone {isFieldMissing('contactTelephone') && <span className="text-danger">*</span>}
                                  </CFormLabel>
                                  <CFormInput
                                    type="tel"
                                    id="contactTelephone"
                                    name="contactTelephone"
                                    value={companyData.contactTelephone}
                                    onChange={handleInputChange}
                                    placeholder="+254 xxx xxx xxx"
                                    invalid={isFieldMissing('contactTelephone')}
                                  />
                                </CCol>
                              </CRow>

                              {/* Contact Person Documents */}
                              <CRow className="mb-4">
                                <CCol md={12}>
                                  <div className="border rounded p-3 bg-light">
                                    <h6 className="fw-bold mb-3">
                                      <CIcon icon={cilUser} className="me-2" />
                                      Contact Person Required Documents
                                    </h6>

                                    <CRow>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="passportPhotos">Two Recent Passport-sized Photographs</CFormLabel>
                                          {renderCertificateStatus('passportPhotos', 'Passport Photos')}
                                          <CFormInput
                                            type="file"
                                            id="passportPhotos"
                                            name="passportPhotos"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            multiple
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload 2 passport-sized photographs (PDF, JPG, or PNG format)</small>
                                          <div className="mt-1">
                                            <small className="text-info">
                                              <CIcon icon={cilWarning} className="me-1" />
                                              You can upload either 2 separate image files or a single PDF containing both photos
                                            </small>
                                          </div>
                                        </div>
                                      </CCol>
                                      <CCol md={6}>
                                        <div className="mb-3">
                                          <CFormLabel htmlFor="nationalId">Copy of National ID/Passport</CFormLabel>
                                          {renderCertificateStatus('nationalIdPassport', 'National ID/Passport Copy')}
                                          <CFormInput
                                            type="file"
                                            id="nationalId"
                                            name="nationalIdPassport"
                                            onChange={handleFileChange}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            disabled={companyStatus === 'approved'}
                                          />
                                          <small className="text-muted">Upload PDF, JPG, or PNG files only</small>
                                        </div>
                                      </CCol>
                                    </CRow>
                                  </div>
                                </CCol>
                              </CRow>

                              {/* Declaration Checkbox */}
                              <CRow className="mb-4">
                                <CCol md={12}>
                                  <div className="border rounded p-3 bg-light">
                                    <CFormCheck
                                      id="declaration"
                                      checked={agreementAccepted}
                                      onChange={(e) => setAgreementAccepted(e.target.checked)}
                                      label="I hereby apply for membership with the Kenya Professional Cleaners Association and declare that the information provided is accurate to the best of my knowledge."
                                      className="fw-bold"
                                    />
                                  </div>
                                  {!agreementAccepted && (
                                    <CAlert color="warning" className="mt-3">
                                      <CIcon icon={cilStar} className="me-2" />
                                      Please accept the declaration to proceed with your membership application.
                                    </CAlert>
                                  )}
                                </CCol>
                              </CRow>

                              {/* Submit Button */}
                              <div className="d-flex gap-2">
                                <CButton
                                  type="submit"
                                  color="primary"
                                  disabled={!agreementAccepted || isLoading || companyStatus === 'approved' || isComplete}
                                  size="lg"
                                >
                                  {isLoading ? (
                                    <>
                                      <CSpinner size="sm" className="me-2" />
                                      Updating...
                                    </>
                                  ) : isComplete ? (
                                    <>
                                      <CIcon icon={cilCheckCircle} className="me-2" />
                                      Application Already Submitted
                                    </>
                                  ) : (
                                    <>
                                      <CIcon icon={cilCheckCircle} className="me-2" />
                                      Submit Membership Application
                                    </>
                                  )}
                                </CButton>
                                <CButton
                                  color="secondary"
                                  variant="outline"
                                  size="lg"
                                  onClick={() => {
                                    // Reset form
                                    if (user) {
                                      setCompanyData({
                                        companyName: user.companyName || user.company || '',
                                        registrationNumber: user.registrationNumber || '',
                                        companyEmail: user.email || '',
                                        companyPhone: user.companyPhone || user.phone || '',
                                        employees: user.employees || '1-10',
                                        address: user.address || '',
                                        physicalLocation: user.physicalLocation || '',
                                        contactPerson: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '',
                                        position: user.position || '',
                                        contactTelephone: user.phone || ''
                                      })
                                    }
                                    // Form reset - toast notifications will handle user feedback
                                  }}
                                >
                                  Reset
                                </CButton>
                              </div>
                            </CForm>
                          </CCol>
                        </CRow>
                      </CTabPanel>

                      {/* Payment Tab */}
                      <CTabPanel
                        className="py-4"
                        itemKey="payment"
                        style={{
                          backgroundColor: activeTab === 'payment' ? '#ffffff' : 'transparent',
                          borderRadius: '12px',
                          border: activeTab === 'payment' ? '2px solid #198754' : 'none',
                          boxShadow: activeTab === 'payment' ? '0 8px 25px rgba(25, 135, 84, 0.15)' : 'none',
                          transition: 'all 0.3s ease',
                          padding: '2rem',
                          margin: '0.5rem 0'
                        }}
                      >
                        <CRow>
                          <CCol md={12}>
                            <div className="mb-4">
                              <div
                                className="p-3 rounded-3 mb-4"
                                style={{
                                  background: 'linear-gradient(135deg, #198754 0%, #20c997 100%)',
                                  color: 'white',
                                  boxShadow: '0 4px 15px rgba(25, 135, 84, 0.3)'
                                }}
                              >
                                <h4 className="mb-2">
                                  <CIcon icon={cilCreditCard} className="me-2" />
                                  Payment Information
                                </h4>
                                <p className="mb-0 opacity-75">
                                  Complete your membership payment using the methods below
                                </p>
                              </div>

                              {isLoadingFees ? (
                                <div className="text-center py-4">
                                  <CSpinner color="primary" />
                                  <p className="text-muted mt-2">Loading payment information...</p>
                                </div>
                              ) : (
                                <>
                                  {/* Payment Instructions - 100% Width */}
                                  <div className="mb-4">
                                    <h5 className="text-success mb-3">
                                      <CIcon icon={cilWarning} className="me-2" />
                                      Payment Instructions
                                    </h5>
                                    <div className="alert alert-info">
                                      <CRow>
                                        <CCol md={8}>
                                          <h6 className="alert-heading mb-3">How to Make Payment:</h6>
                                          <ul className="mb-0">
                                            <li><strong>M-Pesa:</strong> Use paybill <code>522533</code> with account <code>7829305</code></li>
                                            <li><strong>Bank Transfer:</strong> Use account number <code>1325275859</code> (KES Bank)</li>
                                            <li>Include your company name as payment reference</li>
                                            <li>Keep payment receipts for verification</li>
                                            <li>Contact KPCA after payment for confirmation</li>
                                            <li>Add payment information using the button below</li>
                                          </ul>
                                        </CCol>
                                        <CCol md={4}>
                                          <h6 className="alert-heading mb-3">Payment Amounts:</h6>
                                          {membershipFees.length > 0 ? (
                                            membershipFees.map((fee) => (
                                              <div key={fee.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-white rounded">
                                                <span className="text-capitalize fw-bold">{fee.payment_type}:</span>
                                                <span className="fw-bold text-success">
                                                  {parseFloat(fee.amount).toLocaleString()} {fee.currency}
                                                </span>
                                              </div>
                                            ))
                                          ) : (
                                            <div className="text-center py-3">
                                              <small className="text-muted">No fees available</small>
                                            </div>
                                          )}
                                        </CCol>
                                      </CRow>
                                    </div>
                                  </div>

                                  {/* Add Payment Button - Prominent Display */}
                                  <div className="text-center mb-4">
                                    <CButton
                                      color="success"
                                      size="lg"
                                      onClick={() => setShowPaymentModal(true)}
                                      className="px-5 py-3"
                                      style={{
                                        fontSize: '1.1rem',
                                        fontWeight: '600',
                                        boxShadow: '0 4px 15px rgba(25, 135, 84, 0.3)'
                                      }}
                                    >
                                      <CIcon icon={cilCreditCard} className="me-2" size="lg" />
                                      Add Payment Information
                                    </CButton>
                                  </div>

                                  {/* Payment History Table */}
                                  <div className="mt-4">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                      <h5 className="text-primary mb-0">
                                        <CIcon icon={cilDescription} className="me-2" />
                                        Payment History
                                      </h5>
                                      <CBadge color="info" className="px-3 py-2">
                                        {paymentHistory.length} Payment{paymentHistory.length !== 1 ? 's' : ''} Recorded
                                      </CBadge>
                                    </div>

                                    {isLoadingPaymentHistory ? (
                                      <div className="text-center py-5">
                                        <CSpinner color="primary" />
                                        <p className="text-muted mt-2">Loading payment history...</p>
                                      </div>
                                    ) : paymentHistory.length > 0 ? (
                                      <div className="card">
                                        <div className="card-body p-0">
                                          <div className="table-responsive">
                                            <table className="table table-hover mb-0">
                                              <thead className="table-light">
                                                <tr>
                                                  <th className="border-0">#</th>
                                                  <th className="border-0">Payment Type</th>
                                                  <th className="border-0">Amount</th>
                                                  <th className="border-0">Method</th>
                                                  <th className="border-0">Reference</th>
                                                  <th className="border-0">Date</th>
                                                  <th className="border-0">Status</th>
                                                  <th className="border-0">Year</th>
                                                  <th className="border-0">Actions</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {paymentHistory.map((payment, index) => (
                                                  <tr key={payment.id || index}>
                                                    <td className="fw-bold text-muted">{index + 1}</td>
                                                    <td>
                                                      <div className="d-flex align-items-center">
                                                        <CIcon
                                                          icon={cilCreditCard}
                                                          className="me-2 text-primary"
                                                          size="sm"
                                                        />
                                                        <span className="text-capitalize">
                                                          {payment.payment_type.replace(/_/g, ' ')}
                                                        </span>
                                                      </div>
                                                    </td>
                                                    <td>
                                                      <span className="fw-bold text-success">
                                                        KES {parseFloat(payment.payment_amount).toLocaleString()}
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <span className="text-capitalize">
                                                        {payment.payment_method.replace(/_/g, ' ')}
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <code className="bg-light px-2 py-1 rounded small">
                                                        {payment.payment_reference}
                                                      </code>
                                                    </td>
                                                    <td>
                                                      <div>
                                                        <div>{new Date(payment.payment_date).toLocaleDateString()}</div>
                                                        <small className="text-muted">
                                                          {new Date(payment.created_at).toLocaleTimeString()}
                                                        </small>
                                                      </div>
                                                    </td>
                                                    <td>
                                                      <CBadge
                                                        color={
                                                          payment.payment_status === 'verified' ? 'success' :
                                                            payment.payment_status === 'pending' ? 'warning' :
                                                              payment.payment_status === 'rejected' ? 'danger' : 'secondary'
                                                        }
                                                        className="px-3 py-2"
                                                      >
                                                        <CIcon
                                                          icon={
                                                            payment.payment_status === 'verified' ? cilCheckCircle :
                                                              payment.payment_status === 'pending' ? cilWarning :
                                                                cilWarning
                                                          }
                                                          className="me-1"
                                                          size="sm"
                                                        />
                                                        {payment.payment_status === 'verified' ? 'Approved' :
                                                          payment.payment_status === 'pending' ? 'Pending' :
                                                            payment.payment_status === 'rejected' ? 'Rejected' :
                                                              payment.payment_status.charAt(0).toUpperCase() + payment.payment_status.slice(1)}
                                                      </CBadge>
                                                    </td>
                                                    <td>
                                                      <CBadge color="outline-primary" className="text-dark">
                                                        {payment.membership_year || 'N/A'}
                                                      </CBadge>
                                                    </td>
                                                    <td>
                                                      <CButton
                                                        color="primary"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleEditPayment(payment)}
                                                        disabled={payment.payment_status !== 'pending'}
                                                        title={payment.payment_status !== 'pending' ? 'Can only edit pending payments' : 'Edit payment'}
                                                      >
                                                        <CIcon icon={cilPencil} size="sm" />
                                                      </CButton>
                                                    </td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="card">
                                        <div className="card-body text-center py-5">
                                          <CIcon icon={cilCreditCard} size="3xl" className="text-muted mb-3" />
                                          <h6 className="text-muted">No Payment History</h6>
                                          <p className="text-muted mb-3">
                                            You haven't added any payment information yet. Click the button above to add your first payment.
                                          </p>
                                          <CButton
                                            color="primary"
                                            variant="outline"
                                            onClick={() => setShowPaymentModal(true)}
                                          >
                                            <CIcon icon={cilCreditCard} className="me-2" />
                                            Add Your First Payment
                                          </CButton>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </CCol>
                        </CRow>
                      </CTabPanel>
                    </CTabContent>
                  </CTabs>
                </>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Success Modal */}
      <CModal
        visible={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        size="md"
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader className="border-0 pb-2">
          <CModalTitle className="text-success">
            <CIcon icon={cilCheckCircle} className="me-2" />
            Application Submitted Successfully!
          </CModalTitle>
        </CModalHeader>
        <CModalBody className="pt-0">
          <div className="text-center">
            <div className="mb-3">
              <CIcon icon={cilCheckCircle} size="3xl" className="text-success" />
            </div>
            <h5 className="text-success mb-3">Application Complete!</h5>
            <p className="text-muted mb-4">
              Your documents have been uploaded and your application is being processed.
            </p>

            <CAlert color="success" className="mb-4">
              <div className="d-flex align-items-center mb-2">
                <CIcon icon={cilCreditCard} className="me-2" />
                <strong>Complete Your Payment</strong>
              </div>
              <p className="mb-3">Use the payment details below:</p>
              <CRow className="text-center">
                <CCol xs={6}>
                  <div className="border-end">
                    <small className="text-muted">M-Pesa Paybill</small>
                    <div className="fw-bold font-monospace">522533</div>
                  </div>
                </CCol>
                <CCol xs={6}>
                  <small className="text-muted">Account Number</small>
                  <div className="fw-bold font-monospace">7829305</div>
                </CCol>
              </CRow>
            </CAlert>

            <div className="bg-light rounded p-3">
              <small className="text-muted">
                <strong>Registration:</strong> KES 5,000 | <strong>Subscription:</strong> KES 40,000
              </small>
            </div>
          </div>
        </CModalBody>
        <CModalFooter className="border-0 pt-0">
          <CButton
            color="success"
            onClick={() => {
              setShowSuccessModal(false)
              setActiveTab('payment')
            }}
            className="w-100"
          >
            <CIcon icon={cilCreditCard} className="me-2" />
            Go to Payment Tab
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Replace Document Modal */}
      <CModal
        visible={showReplaceModal}
        onClose={() => {
          setShowReplaceModal(false)
          setSelectedDocumentToReplace(null)
          setReplacementFile(null)
        }}
        size="md"
      >
        <CModalHeader>
          <CModalTitle>Replace Document</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {selectedDocumentToReplace && (
            <div>
              <p className="mb-3">
                Replace <strong>{selectedDocumentToReplace.name}</strong> with a new document.
              </p>

              <CFormLabel htmlFor="replacementFile">Select New Document</CFormLabel>
              <CFormInput
                type="file"
                id="replacementFile"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setReplacementFile(e.target.files[0])}
                className="mb-3"
              />

              {replacementFile && (
                <CAlert color="info">
                  <strong>Selected File:</strong> {replacementFile.name}<br />
                  <strong>Size:</strong> {(replacementFile.size / 1024 / 1024).toFixed(2)} MB
                </CAlert>
              )}
            </div>
          )}
        </CModalBody>
        <CModalFooter>
          <CButton
            color="secondary"
            variant="outline"
            onClick={() => {
              setShowReplaceModal(false)
              setSelectedDocumentToReplace(null)
              setReplacementFile(null)
            }}
          >
            Cancel
          </CButton>
          <CButton
            color="warning"
            onClick={async () => {
              if (!replacementFile || !selectedDocumentToReplace) return

              try {
                setIsLoading(true)

                // Upload the replacement document
                const documentType = documentTypeMapping[selectedDocumentToReplace.key]
                const result = await uploadSingleDocument(
                  selectedDocumentToReplace.key,
                  replacementFile,
                  documentType
                )

                if (result.success) {
                  toast.success(`${selectedDocumentToReplace.name} replaced successfully!`, {
                    position: "top-right",
                    autoClose: 4000,
                  })

                  // Refresh profile completion data
                  setTimeout(() => {
                    refreshCompletion()
                  }, 1000)

                  // Close modal
                  setShowReplaceModal(false)
                  setSelectedDocumentToReplace(null)
                  setReplacementFile(null)
                } else {
                  toast.error(`Failed to replace ${selectedDocumentToReplace.name}`, {
                    position: "top-right",
                    autoClose: 6000,
                  })
                }
              } catch (error) {
                console.error('Error replacing document:', error)
                toast.error('Failed to replace document', {
                  position: "top-right",
                  autoClose: 6000,
                })
              } finally {
                setIsLoading(false)
              }
            }}
            disabled={!replacementFile || isLoading}
          >
            {isLoading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Replacing...
              </>
            ) : (
              <>
                <CIcon icon={cilCloudUpload} className="me-2" />
                Replace Document
              </>
            )}
          </CButton>
        </CModalFooter>
      </CModal>

      {/* Add Payment Information Modal */}
      <CModal
        visible={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilCreditCard} className="me-2" />
            Add Payment Information
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CForm onSubmit={handlePaymentSubmit}>
            <CRow>
              {/* Left Column */}
              <CCol md={6}>
                <div className="mb-3">
                  <CFormLabel htmlFor="payment_type">
                    Payment Type <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="payment_type"
                    name="payment_type"
                    value={paymentData.payment_type}
                    onChange={handlePaymentInputChange}
                    required
                  >
                    <option value="new_member">New Member</option>
                    <option value="registration">Registration</option>
                    <option value="subscription">Subscription</option>
                    <option value="renewal">Renewal</option>
                  </CFormSelect>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="payment_amount">
                    Payment Amount (KES) <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    id="payment_amount"
                    name="payment_amount"
                    value={paymentData.payment_amount}
                    onChange={handlePaymentInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="payment_date">
                    Payment Date <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="date"
                    id="payment_date"
                    name="payment_date"
                    value={paymentData.payment_date}
                    onChange={handlePaymentInputChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="bank_name">Bank Name</CFormLabel>
                  <CFormInput
                    type="text"
                    id="bank_name"
                    name="bank_name"
                    value={paymentData.bank_name}
                    onChange={handlePaymentInputChange}
                    placeholder="e.g., KCB Bank"
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="receipt_number">Receipt Number</CFormLabel>
                  <CFormInput
                    type="text"
                    id="receipt_number"
                    name="receipt_number"
                    value={paymentData.receipt_number}
                    onChange={handlePaymentInputChange}
                    placeholder="Receipt or invoice number"
                  />
                </div>
              </CCol>

              {/* Right Column */}
              <CCol md={6}>
                <div className="mb-3">
                  <CFormLabel htmlFor="payment_method">
                    Payment Method <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="payment_method"
                    name="payment_method"
                    value={paymentData.payment_method}
                    onChange={handlePaymentInputChange}
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">M-Pesa</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Online</option>
                    <option value="other">Other</option>
                  </CFormSelect>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="payment_reference">
                    Payment Reference <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="payment_reference"
                    name="payment_reference"
                    value={paymentData.payment_reference}
                    onChange={handlePaymentInputChange}
                    placeholder="e.g., M-Pesa code, Bank reference, Receipt number"
                    required
                  />
                  <small className="text-muted">Enter unique transaction reference or receipt number</small>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="account_number">Account Number</CFormLabel>
                  <CFormInput
                    type="text"
                    id="account_number"
                    name="account_number"
                    value={paymentData.account_number}
                    onChange={handlePaymentInputChange}
                    placeholder="Account number or M-Pesa number"
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="membership_year">Membership Year</CFormLabel>
                  <CFormInput
                    type="number"
                    id="membership_year"
                    name="membership_year"
                    value={paymentData.membership_year}
                    onChange={handlePaymentInputChange}
                    min="2020"
                    max="2030"
                  />
                </div>
              </CCol>
            </CRow>

            {/* Additional Notes - Full Width */}
            <CRow>
              <CCol md={12}>
                <div className="mb-3">
                  <CFormLabel htmlFor="additional_notes">Additional Notes</CFormLabel>
                  <CFormTextarea
                    id="additional_notes"
                    name="additional_notes"
                    value={paymentData.additional_notes}
                    onChange={handlePaymentInputChange}
                    placeholder="Any additional payment details or notes..."
                    rows={3}
                  />
                </div>
              </CCol>
            </CRow>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <CButton
                type="button"
                color="secondary"
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
              >
                Cancel
              </CButton>
              <CButton
                type="submit"
                color="success"
                disabled={isLoadingPayment}
              >
                {isLoadingPayment ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <CIcon icon={cilCheckCircle} className="me-2" />
                    Add Payment
                  </>
                )}
              </CButton>
            </div>
          </CForm>
        </CModalBody>
      </CModal>

      {/* Edit Payment Information Modal */}
      <CModal
        visible={showEditPaymentModal}
        onClose={() => {
          setShowEditPaymentModal(false)
          setEditingPayment(null)
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilPencil} className="me-2" />
            Edit Payment Information
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CForm onSubmit={handleEditPaymentSubmit}>
            <CRow>
              {/* Left Column */}
              <CCol md={6}>
                <div className="mb-3">
                  <CFormLabel htmlFor="edit_payment_type">
                    Payment Type <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="edit_payment_type"
                    name="payment_type"
                    value={paymentData.payment_type}
                    onChange={handlePaymentInputChange}
                    required
                  >
                    <option value="new_member">New Member</option>
                    <option value="registration">Registration</option>
                    <option value="subscription">Subscription</option>
                    <option value="renewal">Renewal</option>
                  </CFormSelect>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_payment_amount">
                    Payment Amount (KES) <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    id="edit_payment_amount"
                    name="payment_amount"
                    value={paymentData.payment_amount}
                    onChange={handlePaymentInputChange}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_payment_date">
                    Payment Date <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="date"
                    id="edit_payment_date"
                    name="payment_date"
                    value={paymentData.payment_date}
                    onChange={handlePaymentInputChange}
                    required
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_bank_name">Bank Name</CFormLabel>
                  <CFormInput
                    type="text"
                    id="edit_bank_name"
                    name="bank_name"
                    value={paymentData.bank_name}
                    onChange={handlePaymentInputChange}
                    placeholder="e.g., KCB Bank"
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_receipt_number">Receipt Number</CFormLabel>
                  <CFormInput
                    type="text"
                    id="edit_receipt_number"
                    name="receipt_number"
                    value={paymentData.receipt_number}
                    onChange={handlePaymentInputChange}
                    placeholder="Receipt or invoice number"
                  />
                </div>
              </CCol>

              {/* Right Column */}
              <CCol md={6}>
                <div className="mb-3">
                  <CFormLabel htmlFor="edit_payment_method">
                    Payment Method <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="edit_payment_method"
                    name="payment_method"
                    value={paymentData.payment_method}
                    onChange={handlePaymentInputChange}
                    required
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">M-Pesa</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Online</option>
                    <option value="other">Other</option>
                  </CFormSelect>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_payment_reference">
                    Payment Reference <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormInput
                    type="text"
                    id="edit_payment_reference"
                    name="payment_reference"
                    value={paymentData.payment_reference}
                    onChange={handlePaymentInputChange}
                    placeholder="e.g., M-Pesa code, Bank reference, Receipt number"
                    required
                  />
                  <small className="text-muted">Enter unique transaction reference or receipt number</small>
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_account_number">Account Number</CFormLabel>
                  <CFormInput
                    type="text"
                    id="edit_account_number"
                    name="account_number"
                    value={paymentData.account_number}
                    onChange={handlePaymentInputChange}
                    placeholder="Account number or M-Pesa number"
                  />
                </div>

                <div className="mb-3">
                  <CFormLabel htmlFor="edit_membership_year">Membership Year</CFormLabel>
                  <CFormInput
                    type="number"
                    id="edit_membership_year"
                    name="membership_year"
                    value={paymentData.membership_year}
                    onChange={handlePaymentInputChange}
                    min="2020"
                    max="2030"
                  />
                </div>
              </CCol>
            </CRow>

            {/* Additional Notes - Full Width */}
            <CRow>
              <CCol md={12}>
                <div className="mb-3">
                  <CFormLabel htmlFor="edit_additional_notes">Additional Notes</CFormLabel>
                  <CFormTextarea
                    id="edit_additional_notes"
                    name="additional_notes"
                    value={paymentData.additional_notes}
                    onChange={handlePaymentInputChange}
                    placeholder="Any additional payment details or notes..."
                    rows={3}
                  />
                </div>
              </CCol>
            </CRow>

            {/* Action Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <CButton
                type="button"
                color="secondary"
                variant="outline"
                onClick={() => {
                  setShowEditPaymentModal(false)
                  setEditingPayment(null)
                }}
              >
                Cancel
              </CButton>
              <CButton
                type="submit"
                color="primary"
                disabled={isLoadingEditPayment}
              >
                {isLoadingEditPayment ? (
                  <>
                    <CSpinner size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CIcon icon={cilPencil} className="me-2" />
                    Update Payment
                  </>
                )}
              </CButton>
            </div>
          </CForm>
        </CModalBody>
      </CModal>
    </>
  )
}

export default Profile 